import { Component, OnDestroy, OnInit } from '@angular/core';
import { WorkflowService } from '../service/core/workflow.service';
import { CommonModule, DatePipe } from '@angular/common';
import { TableModule } from 'primeng/table';
import { FluidModule } from 'primeng/fluid';
import { ButtonModule } from 'primeng/button';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { ProgressBarModule } from 'primeng/progressbar';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TagModule } from 'primeng/tag';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { AppDatePipe } from '../service/core/app-date.pipe';
import { SettingsBacklinkComponent } from '../settings/settings-backlink.component';

@Component({
  selector: 'app-Worflows',
  templateUrl: './worflow.html',
  styleUrl: './worflow.scss',
  imports: [CommonModule, DatePipe, TableModule, FluidModule, ButtonModule, FormsModule, ProgressBarModule, ProgressSpinnerModule, TagModule, DialogModule, InputTextModule, TooltipModule, AppDatePipe, SettingsBacklinkComponent]
})
export class Worflows implements OnInit, OnDestroy {
workflowList: any[] = [];
  loading = false;
  activeFilter: 'all' | 'active' | 'draft' = 'all';
  activeTab: 'main' | 'reusable' | 'journey' = 'main';
  searchTerm = '';
  sortBy: 'updatedAt' | 'flowName' | 'status' | 'version' | 'source' = 'updatedAt';
  sortDir: 'asc' | 'desc' = 'desc';

  // Modal State
  showCreateModal = false;
  aiPrompt = '';
  generating = false;
  generationProgress = 0;
  generationElapsedSec = 0;
  generationStatusText = '';
  generationStage: 'THINKING' | 'ANALYZING' | 'CREATING' | 'FINALIZING' = 'THINKING';
  readonly generationTargetSec = 40;
  private generationTimer?: ReturnType<typeof setInterval>;
  private generationStartedAt = 0;

  // Journeys
  journeyList: any[] = [];
  loadingJourneys = false;

  // Subflows (chips)
  subflows: any[] = [];
  selectedFlowNames: string[] = [];
  loadingSubflows = false;

  // Context
  companyId = '8f3a2c7e-4b6a-4a9d-9e2b-1c5f9a7d3b21';

  constructor(
    private workflowService: WorkflowService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.fetchWorkflows();
    this.fetchJourneys();
    const tab = this.route.snapshot.queryParamMap.get('tab');
    if (tab === 'journey') this.activeTab = 'journey';
  }

  ngOnDestroy(): void {
    this.stopGenerationTicker();
  }

  // GETTERS FOR KPI CARDS (Replaces the missing pipe)
  get activeFlowsCount(): number {
    return this.workflowList.filter(wf => wf.status?.toUpperCase() === 'PUBLISHED' || wf.status?.toUpperCase() === 'ACTIVE').length;
  }

  get draftFlowsCount(): number {
    return this.workflowList.filter(wf => wf.status?.toUpperCase() === 'DRAFT').length;
  }

  isReusableSource(source: string): boolean {
    const s = (source ?? '').toUpperCase();
    return s === 'FORKED' || s === 'SYSTEM';
  }

  get mainFlowsCount(): number {
    return this.workflowList.filter(wf => !this.isReusableSource(wf.source)).length;
  }

  get reusableFlowsCount(): number {
    return this.workflowList.filter(wf => this.isReusableSource(wf.source)).length;
  }

  setActiveTab(tab: 'main' | 'reusable' | 'journey'): void {
    this.activeTab = tab;
    this.activeFilter = 'all';
    this.searchTerm = '';
  }

  get journeyCount(): number { return this.journeyList.length; }

  fetchJourneys(): void {
    this.loadingJourneys = true;
    this.workflowService.getJourneys().subscribe({
      next: (res: any) => {
        this.journeyList = res?.data ?? res ?? [];
        this.loadingJourneys = false;
      },
      error: () => { this.loadingJourneys = false; }
    });
  }

  goToCreateJourney(): void {
    this.router.navigate(['/journeys/new']);
  }

  editJourney(journey: any): void {
    this.router.navigate(['/journeys/new'], { queryParams: { id: journey.id } });
  }

  deleteJourney(journey: any): void {
    if (journey.status?.toUpperCase() !== 'DRAFT') return;
    this.workflowService.deleteJourney(journey.id).subscribe({
      next: () => {
        this.journeyList = this.journeyList.filter(j => j.id !== journey.id);
      },
      error: (err) => { console.error('Delete journey failed', err); }
    });
  }

  get filteredWorkflows(): any[] {
    const term = this.searchTerm.trim().toLowerCase();
    const filtered = this.workflowList.filter((wf) => {
      const isReusable = this.isReusableSource(wf.source);
      if (this.activeTab === 'main' && isReusable) return false;
      if (this.activeTab === 'reusable' && !isReusable) return false;

      const status = (wf.status ?? '').toString().toUpperCase();
      if (this.activeFilter === 'active' && !['PUBLISHED', 'ACTIVE'].includes(status)) {
        return false;
      }
      if (this.activeFilter === 'draft' && status !== 'DRAFT') {
        return false;
      }
      if (!term) {
        return true;
      }
      const haystack = [
        wf.flowName,
        wf.status,
        wf.source,
        wf.id
      ]
        .filter(Boolean)
        .map((v: any) => String(v).toLowerCase())
        .join(' ');
      return haystack.includes(term);
    });

    const dir = this.sortDir === 'asc' ? 1 : -1;
    return filtered.sort((a, b) => {
      const aVal = this.getSortValue(a, this.sortBy);
      const bVal = this.getSortValue(b, this.sortBy);
      if (aVal < bVal) return -1 * dir;
      if (aVal > bVal) return 1 * dir;
      return 0;
    });
  }

  private getSortValue(item: any, key: 'updatedAt' | 'flowName' | 'status' | 'version' | 'source'): string | number {
    if (key === 'updatedAt') {
      const time = new Date(item?.updatedAt ?? 0).getTime();
      return Number.isNaN(time) ? 0 : time;
    }
    if (key === 'version') {
      const num = Number(item?.version ?? 0);
      return Number.isNaN(num) ? 0 : num;
    }
    return (item?.[key] ?? '').toString().toLowerCase();
  }

  fetchWorkflows(): void {
    this.loading = true;
    this.workflowService.getCompanyWorkflows().subscribe({
      next: (res: any) => {
        // Map data from the latest response structure: { success: "true", data: [...] }
        this.workflowList = res.data || [];
        if (this.activeFilter !== 'all' && this.filteredWorkflows.length === 0) {
          this.activeFilter = 'all';
        }
        this.loading = false;
      },
      error: (err) => {
        console.error('Workflow fetch failed', err);
        this.loading = false;
      }
    });
  }

  getSeverity(status: string) {
    switch (status?.toUpperCase()) {
      case 'PUBLISHED':
      case 'ACTIVE': return 'success';
      case 'DRAFT': return 'secondary';
      case 'ARCHIVED': return 'danger';
      default: return 'info';
    }
  }

  generateWorkflow(): void {
    if (!this.aiPrompt.trim()) return;
    this.generating = true;
    this.generationStartedAt = Date.now();
    this.startGenerationTicker();

    this.workflowService.createWorkflowFromAi({
      maxRetries: 4,
      userPrompt: this.aiPrompt,
      selectedFlowNames: this.selectedFlowNames
    }).subscribe({
      next: () => {
        this.finishGenerationFlow();
      },
      error: () => {
        this.generating = false;
        this.stopGenerationTicker();
      }
    });
  }

  goToWorkflow(workflow: any): void {
    const companyId = workflow.companyId ?? this.companyId;
    this.router.navigate(['/workflows', companyId, workflow.flowName]);
  }

  openCreateModal(): void {
    this.showCreateModal = true;
    this.selectedFlowNames = [];
    this.loadSubflows();
  }

  loadSubflows(): void {
    this.loadingSubflows = true;
    this.workflowService.getSubflows().subscribe({
      next: (res: any) => {
        this.subflows = res?.data ?? res ?? [];
        this.loadingSubflows = false;
      },
      error: () => {
        this.subflows = [];
        this.loadingSubflows = false;
      }
    });
  }

  toggleSubflow(flowName: string): void {
    const idx = this.selectedFlowNames.indexOf(flowName);
    if (idx === -1) {
      this.selectedFlowNames.push(flowName);
    } else {
      this.selectedFlowNames.splice(idx, 1);
    }
  }

  isSubflowSelected(flowName: string): boolean {
    return this.selectedFlowNames.includes(flowName);
  }

  closeCreateModal(): void {
    if (this.generating) return;
    this.showCreateModal = false;
  }

  setStatusFilter(filter: 'all' | 'active' | 'draft'): void {
    this.activeFilter = filter;
  }

  toggleSortDir(): void {
    this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
  }

  setSort(key: 'updatedAt' | 'flowName' | 'status' | 'version' | 'source'): void {
    if (this.sortBy === key) {
      this.toggleSortDir();
      return;
    }
    this.sortBy = key;
    this.sortDir = 'desc';
  }

  getSortIcon(key: 'updatedAt' | 'flowName' | 'status' | 'version' | 'source'): string {
    if (this.sortBy !== key) {
      return 'pi-sort-alt';
    }
    return this.sortDir === 'asc' ? 'pi-sort-amount-up-alt' : 'pi-sort-amount-down';
  }

  private startGenerationTicker(): void {
    this.stopGenerationTicker();
    this.generationElapsedSec = 0;
    this.generationProgress = 0;
    this.generationStatusText = 'Thinking through your prompt...';
    this.generationStage = 'THINKING';

    this.generationTimer = setInterval(() => {
      const elapsed = Math.floor((Date.now() - this.generationStartedAt) / 1000);
      this.generationElapsedSec = Math.min(this.generationTargetSec, elapsed);
      this.generationProgress = Math.min(
        98,
        Math.round((this.generationElapsedSec / this.generationTargetSec) * 100)
      );

      if (elapsed < 12) {
        this.generationStage = 'THINKING';
        this.generationStatusText = 'Thinking through your prompt...';
      } else if (elapsed < 26) {
        this.generationStage = 'ANALYZING';
        this.generationStatusText = 'Analyzing requirements and branch logic...';
      } else if (elapsed < this.generationTargetSec) {
        this.generationStage = 'CREATING';
        this.generationStatusText = 'Creating an ideal workflow architecture...';
      } else {
        this.generationStage = 'FINALIZING';
        this.generationStatusText = 'Finalizing generated workflow...';
      }
    }, 250);
  }

  private stopGenerationTicker(): void {
    if (this.generationTimer) {
      clearInterval(this.generationTimer);
      this.generationTimer = undefined;
    }
  }

  private finishGenerationFlow(): void {
    const elapsedMs = Date.now() - this.generationStartedAt;
    const remainingMs = Math.max(0, this.generationTargetSec * 1000 - elapsedMs);

    setTimeout(() => {
      this.generationStage = 'FINALIZING';
      this.generationStatusText = 'Finalizing generated workflow...';
      this.generationProgress = 100;

      setTimeout(() => {
        this.generating = false;
        this.stopGenerationTicker();
        this.showCreateModal = false;
        this.aiPrompt = '';
        this.selectedFlowNames = [];
        this.fetchWorkflows();
      }, 400);
    }, remainingMs);
  }
}
